import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Calendar, Home, Video, Store, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const STATUS_COLORS = {
  requested: "bg-amber-100 text-amber-900",
  confirmed: "bg-emerald-100 text-emerald-900",
  rescheduled: "bg-blue-100 text-blue-900",
  completed: "bg-slate-200 text-slate-800",
  cancelled: "bg-red-100 text-red-900",
  no_show: "bg-red-100 text-red-900",
};
const TYPE_ICON = { shop_visit: Store, home_visit: Home, video_call: Video };
const STATUSES = ["", "requested", "confirmed", "rescheduled", "completed", "cancelled", "no_show"];

export default function AdminAppointments() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null);

  const typeById = useMemo(() => Object.fromEntries(types.map((t) => [t.id, t])), [types]);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [a, t] = await Promise.all([
        api.get(`/admin/stitching/appointments${filter ? `?status=${filter}` : ""}`),
        api.get("/admin/stitching/clothing-types"),
      ]);
      setRows(a.data.items || []);
      setTotal(a.data.total || 0);
      setTypes(t.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this appointment?")) return;
    try {
      await api.post(`/admin/stitching/appointments/${id}/cancel`, { note: "Cancelled by admin" });
      toast.success("Cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="admin-appointments-page">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Stitching</div>
          <h1 className="font-display text-4xl">Appointments</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">{total} total · showing {rows.length}</p>
        </div>
        <div>
          <label className="overline block mb-1">Filter by status</label>
          <select className="kb-input min-w-[200px]" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="appointment-filter">
            {STATUSES.map((s) => <option key={s || "any"} value={s}>{s || "All statuses"}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-[hsl(var(--kb-muted))]" data-testid="admin-appointments-loading">Loading…</div>
      ) : error ? (
        <div className="p-8 text-center text-red-700" data-testid="admin-appointments-error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[hsl(var(--kb-border))] py-14 text-center">
          <Calendar size={28} className="mx-auto text-[hsl(var(--kb-muted))] mb-3" />
          <div className="font-display text-xl">No appointments yet</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((a) => {
            const Icon = TYPE_ICON[a.appointment_type] || Calendar;
            return (
              <div key={a.id} className="bg-white border border-[hsl(var(--kb-border))] p-4 flex gap-4 flex-wrap" data-testid={`admin-appointment-${a.id}`}>
                <div className="p-2 bg-[hsl(var(--kb-surface-alt))] self-start">
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg">{typeById[a.clothing_type_id]?.name || "–"}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_COLORS[a.status]}`}>
                      {a.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="text-xs text-[hsl(var(--kb-muted))] mt-1 flex items-center gap-3 flex-wrap">
                    <span><Calendar size={10} className="inline mr-0.5" /> {a.confirmed_date || a.preferred_date} · {a.confirmed_time_slot || a.preferred_time_slot}</span>
                    <span><User size={10} className="inline mr-0.5" /> {a.contact_number}</span>
                    {a.address?.city && <span>{a.address.line1}, {a.address.city}</span>}
                  </div>
                  {a.notes && <div className="text-xs italic text-[hsl(var(--kb-muted))] mt-2">"{a.notes}"</div>}
                </div>
                <div className="flex gap-2 items-start">
                  {a.status === "requested" || a.status === "rescheduled" ? (
                    <button
                      onClick={() => setConfirmTarget(a)}
                      className="kb-btn-outline !py-1.5 !px-3 !text-xs inline-flex items-center gap-1"
                      data-testid={`confirm-appointment-${a.id}`}
                    >
                      <CheckCircle2 size={12} /> Confirm
                    </button>
                  ) : null}
                  {!["completed", "cancelled"].includes(a.status) && (
                    <button
                      onClick={() => cancel(a.id)}
                      className="text-red-700 !py-1.5 !px-3 !text-xs inline-flex items-center gap-1 hover:bg-red-50"
                      data-testid={`cancel-admin-appointment-${a.id}`}
                    >
                      <XCircle size={12} /> Cancel
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog target={confirmTarget} onClose={() => setConfirmTarget(null)} onSaved={() => { setConfirmTarget(null); load(); }} />
    </div>
  );
}

function ConfirmDialog({ target, onClose, onSaved }) {
  const [form, setForm] = useState({
    confirmed_date: "",
    confirmed_time_slot: "",
    assigned_staff_name: "",
    service_charge: "",
    note: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setForm({
      confirmed_date: target.confirmed_date || target.preferred_date || "",
      confirmed_time_slot: target.confirmed_time_slot || target.preferred_time_slot || "",
      assigned_staff_name: target.assigned_staff_name || "",
      service_charge: target.service_charge || "",
      note: "",
    });
  }, [target]);

  const save = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await api.post(`/admin/stitching/appointments/${target.id}/confirm`, {
        confirmed_date: form.confirmed_date,
        confirmed_time_slot: form.confirmed_time_slot,
        assigned_staff_name: form.assigned_staff_name || null,
        service_charge: form.service_charge === "" ? null : Number(form.service_charge),
        note: form.note.trim() || null,
      });
      toast.success("Confirmed");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="admin-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Confirm appointment</DialogTitle>
          <DialogDescription>Lock the date, time, staff, and service charge.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <input type="date" className="kb-input" value={form.confirmed_date} onChange={(e) => setForm({ ...form, confirmed_date: e.target.value })} data-testid="confirm-date" />
          <input className="kb-input" placeholder="Time slot e.g. 10:00-12:00" value={form.confirmed_time_slot} onChange={(e) => setForm({ ...form, confirmed_time_slot: e.target.value })} data-testid="confirm-slot" />
          <input className="kb-input" placeholder="Assigned staff (optional)" value={form.assigned_staff_name} onChange={(e) => setForm({ ...form, assigned_staff_name: e.target.value })} />
          <input type="number" className="kb-input" placeholder="Service charge (₹)" value={form.service_charge} onChange={(e) => setForm({ ...form, service_charge: e.target.value })} data-testid="confirm-service-charge" />
          <textarea rows={2} className="kb-input" placeholder="Internal note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </div>
        <DialogFooter>
          <button className="kb-btn-outline" onClick={onClose} type="button">Cancel</button>
          <button className="kb-btn-primary" onClick={save} disabled={saving} data-testid="confirm-save-btn" type="button">
            {saving ? "Saving…" : "Confirm"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
